import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { useLocation, useNavigate, useNavigationType, type NavigationType } from 'react-router-dom';

interface SidebarHistoryValue {
  canGoBack: boolean;
  canGoForward: boolean;
  go: (path: string, options?: { passive?: boolean }) => void;
  getIntentVersion: () => number;
  goBack: () => void;
  goForward: () => void;
}

interface HistoryEntry {
  key: string;
  path: string;
}

interface HistoryState {
  entries: HistoryEntry[];
  // The browser's position, which a POP moves before the router reports it.
  index: number;
}

interface LocationReport {
  entry: HistoryEntry;
  type: NavigationType;
}

const SidebarHistoryContext = createContext<SidebarHistoryValue | null>(null);

function record(history: HistoryState, { entry, type }: LocationReport): HistoryState {
  if (type === 'POP') {
    const index = history.entries.findIndex((candidate) => candidate.key === entry.key);
    if (index === history.index) return history;
    if (index !== -1) return { ...history, index };
  }
  if (type === 'REPLACE') {
    const entries = [...history.entries];
    entries[history.index] = entry;
    return { entries, index: history.index };
  }
  const entries = [...history.entries.slice(0, history.index + 1), entry];
  return { entries, index: entries.length - 1 };
}

export function SidebarHistoryProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const report: LocationReport = {
    entry: { key: location.key, path: `${location.pathname}${location.search}` },
    type: navigationType
  };
  const latestReport = useRef(report);
  latestReport.current = report;
  const [history, setHistory] = useState<HistoryState>(() => ({
    entries: [report.entry],
    index: 0
  }));
  const browserHistory = useRef(history);
  const recordedKey = useRef(location.key);
  // The router reports a PUSH only once its loaders finish, and until then the
  // browser has not moved. Tracking the destination lets a later choice tell
  // "already heading there" from "already there".
  const pendingPushPath = useRef<string | null>(null);
  const intentVersion = useRef(0);
  const getIntentVersion = useCallback(() => intentVersion.current, []);

  // Entries come from the router's reports; only a Back or Forward moves the
  // index ahead of its report, because a POP moves the browser before its loader
  // runs. Handlers record the rendered location first because a child's layout
  // effect runs before this provider's, so the stack may not include it yet.
  const recordLocation = useCallback(() => {
    const latest = latestReport.current;
    if (recordedKey.current !== latest.entry.key) {
      recordedKey.current = latest.entry.key;
      pendingPushPath.current = null;
      browserHistory.current = record(browserHistory.current, latest);
    }
    return browserHistory.current;
  }, []);

  // A layout effect keeps the back/forward enabled state in the same commit as the location.
  useLayoutEffect(() => {
    setHistory(recordLocation());
  }, [location.key, recordLocation]);

  const go = useCallback(
    (path: string, options?: { passive?: boolean }) => {
      // Choosing the visible route is still explicit intent. In particular,
      // startup restoration must not reclaim it after this click's render.
      if (!options?.passive) intentVersion.current += 1;
      const current = recordLocation();
      const browserPath = current.entries[current.index].path;
      if ((pendingPushPath.current ?? browserPath) === path) return;
      if (browserPath === path) {
        // Only an uncommitted PUSH stands between the browser and this path.
        // Replacing the entry aborts its loader without a duplicate entry.
        pendingPushPath.current = null;
        navigate(path, { replace: true });
        return;
      }
      pendingPushPath.current = path;
      navigate(path);
    },
    [navigate, recordLocation]
  );

  const move = useCallback(
    (direction: -1 | 1) => {
      intentVersion.current += 1;
      const current = recordLocation();
      const index = current.index + direction;
      if (index < 0 || index >= current.entries.length) return;
      // A POP moves the browser's index before its loader runs, and supersedes an
      // uncommitted PUSH, so a tab chosen meanwhile pushes from the new position.
      pendingPushPath.current = null;
      browserHistory.current = { ...current, index };
      setHistory(browserHistory.current);
      navigate(direction);
    },
    [navigate, recordLocation]
  );
  const goBack = useCallback(() => move(-1), [move]);
  const goForward = useCallback(() => move(1), [move]);

  const canGoBack = history.index > 0;
  const canGoForward = history.index < history.entries.length - 1;

  const value = useMemo(
    () => ({ canGoBack, canGoForward, go, getIntentVersion, goBack, goForward }),
    [canGoBack, canGoForward, go, getIntentVersion, goBack, goForward]
  );

  return <SidebarHistoryContext.Provider value={value}>{children}</SidebarHistoryContext.Provider>;
}

export function useSidebarHistory(): SidebarHistoryValue {
  const value = useContext(SidebarHistoryContext);
  if (!value) throw new Error('useSidebarHistory must be used inside SidebarHistoryProvider.');
  return value;
}
